import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MessageCircle, HelpCircle } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { SessionForm } from '../components/SessionForm'
import { apiUrl } from '../config/api'

const languages = [
  'English', 'Spanish', 'French', 'German', 'Arabic', 'Hindi',
  'Chinese', 'Japanese', 'Korean', 'Danish', 'Urdu'
]

export const HomePage: React.FC = () => {
  const navigate = useNavigate()
  const [isCreatingSession, setIsCreatingSession] = useState(false)

  const handleCreateSession = async (ownerLanguage: string, guestLanguage: string) => {
    setIsCreatingSession(true)
    
    try {
      const response = await fetch(apiUrl('/api/sessions'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ownerLanguage,
          guestLanguage
        })
      })

      if (!response.ok) {
        throw new Error('Failed to create session')
      }

      const data = await response.json()
      toast.success('Session created successfully!')
      
      // Clear any existing guest flags when creating a new session
      // This ensures the creator is treated as the owner
      sessionStorage.removeItem(`guest_${data.session.id}`)
      
      navigate(`/session/${data.session.id}`)
    } catch (error) {
      console.error('Error creating session:', error)
      toast.error('Failed to create session. Please try again.')
    } finally {
      setIsCreatingSession(false)
    }
  }

  const openUserGuide = () => {
    window.open('/guide', '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes')
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl">
              <MessageCircle className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            CrossTalk
          </h1>
          <p className="text-gray-600 mb-4">
            Real-time translation chat for travelers
          </p>
          
          {/* User Guide Button */}
          <button
            onClick={openUserGuide}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
          >
            <HelpCircle className="w-4 h-4" />
            How to Use CrossTalk
          </button>
        </div>
        
        {/* Session Creation Form */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <SessionForm 
            languages={languages}
            onSubmit={handleCreateSession}
            isLoading={isCreatingSession}
          />
        </div>
      </div>
    </div>
  )
}